import { DateTime } from 'luxon';
import { nanoid } from 'nanoid';
import type { Channel } from '@getaccept/pusher';
import { ConversationEvent, DealroomEvent } from '@getaccept/pusher';
import type {
  Conversation,
  Message,
  MessageMetadata,
  Participant,
} from '@getaccept/dsr-shared-new';
import { ChatHelper, ConversationType, MessageType } from '@getaccept/dsr-shared-new';
import { t } from '@getaccept/lib-shared-new/src/helpers/translation.helper';
import { defineStore } from 'pinia';
import { useToast } from '@getaccept/lib-shared-new/src/toast';
import type { Ref } from 'vue';
import { ref } from 'vue';
import type {
  ChatParticipant,
  ChatParticipantId,
  OpenConversationPayload,
  Typing,
} from '@getaccept/dsr-shared-new/src/chat/types';
import { ChatService } from '../../../api/chat/chat.service';
import { pusherClient } from '../../../api/pusher/pusher-client';

export const useChatPublishedStore = defineStore('chatPublished', () => {
  const { toast } = useToast();

  const conversation: Ref<Conversation> = ref(null);
  const conversations: Ref<Conversation[]> = ref([]);
  const replyMessage: Ref<Message> = ref(null);
  const typing: Ref<Typing[]> = ref([]);
  const error = ref(false);
  const loadingConversationList = ref(true);
  const loadingConversationDetails = ref(true);

  const setDefaultConversation = () => {
    conversation.value = conversations.value.find(({ type }) => type === ConversationType.General);
  };

  const setCurrentConversation = (conversationId: string) => {
    conversation.value = conversations.value.find(({ id }) => id === conversationId);
  };

  const setReplyMessage = (message: Message) => {
    replyMessage.value = message;
  };

  const addMessage = (
    newMessage: Message,
    id: string,
    conversationToUpdate: Conversation,
    replace?: boolean
  ) => {
    const updatedConversation = ChatHelper.updateConversation(
      conversationToUpdate,
      newMessage,
      id,
      replace
    );

    if (conversation.value.id === updatedConversation.id) {
      conversation.value = updatedConversation;
    }

    conversations.value = ChatHelper.mapConversations(conversations.value, updatedConversation);
  };

  const newConversationParticipants = ref<ChatParticipantId[]>([]);

  const handleNewConversationParticipants = (participants: ChatParticipant[]) => {
    newConversationParticipants.value = ChatHelper.formatNewParticipants(participants);
  };

  const openConversationPayload = ref<OpenConversationPayload>(null);

  const setOpenConversationPayload = (payload: OpenConversationPayload) => {
    openConversationPayload.value = payload;
  };

  const loadConversationList = async () => {
    error.value = false;
    loadingConversationList.value = false;
    try {
      conversations.value = await ChatService.loadConversationList();
    } catch {
      error.value = true;
    } finally {
      loadingConversationList.value = false;
    }
  };

  const loadConversationDetails = async () => {
    error.value = false;
    loadingConversationDetails.value = true;
    try {
      conversations.value = await Promise.all(
        conversations.value.map(
          async conversation => await ChatService.loadConversationDetails(conversation.id)
        )
      );
    } catch {
      error.value = true;
    } finally {
      loadingConversationDetails.value = false;
    }
  };

  const createConversation = async (participantId: string, text: string) => {
    try {
      let existingConversationId = ChatHelper.getExistingPrivateConversationId(
        conversations.value,
        [...newConversationParticipants.value, { participantId }]
      );

      if (!existingConversationId) {
        const newConversation = await ChatService.createConversation(
          newConversationParticipants.value
        );

        conversations.value.push({
          ...newConversation,
          messages: [],
        });

        existingConversationId = newConversation.id;
      }

      setCurrentConversation(existingConversationId);
      ChatHelper.scrollConversationIntoView();
    } catch {
      error.value = true;
    } finally {
      createMessage(text, participantId);
    }
  };

  const createMessage = async (text: string, participantId: string) => {
    if (!conversation.value) {
      return;
    }

    const metadata: MessageMetadata = replyMessage.value
      ? { repliedTo: replyMessage.value.id, mentions: [] }
      : undefined;

    const tempMessage = {
      id: nanoid(),
      text,
      owner: {
        participantId,
      },
      metadata,
      createdAt: DateTime.now().toMillis(),
      isDraft: true,
      type: MessageType.Public,
      reads: [],
    };

    const updatedConversation = {
      ...conversation.value,
      messages: [...conversation.value.messages, tempMessage],
    };
    conversation.value = updatedConversation;
    conversations.value = ChatHelper.mapConversations(conversations.value, updatedConversation);

    try {
      const message = await ChatService.addMessage(
        updatedConversation.id,
        text,
        replyMessage.value?.id
      );
      addMessage(message, tempMessage.id, updatedConversation, true);
    } catch (e) {
      toast.danger(t('dr_could_not_send_message'));
      const errorMessage = { ...tempMessage, error: true };
      addMessage(errorMessage, tempMessage.id, updatedConversation, true);
    }
  };

  const retrySend = async (messageId: string) => {
    const message = conversation.value.messages.find(message => message.id === messageId);

    if (!message) {
      return;
    }

    const updatedConversation = {
      ...conversation.value,
      messages: conversation.value.messages.map(message =>
        message.id === messageId ? { ...message, error: false } : message
      ),
    };

    conversations.value = ChatHelper.mapConversations(conversations.value, updatedConversation);

    try {
      const newMessage = await ChatService.addMessage(
        updatedConversation.id,
        message.text,
        message.metadata?.repliedTo
      );

      addMessage(newMessage, messageId, updatedConversation, true);
    } catch (e) {
      toast.danger(t('dr_could_not_send_message'));
    }
  };

  const setMessageReadBy = async (messageId: string) => {
    try {
      await ChatService.setMessageReadBy(conversation.value.id, messageId);
    } catch (e) {
      console.error(e);
    }
  };

  const setTypingStatus = async (isTyping: boolean) => {
    if (!conversation.value) {
      return;
    }
    try {
      await ChatService.setTypingStatus(conversation.value.id, isTyping);
    } catch (e) {
      console.error(e);
    }
  };

  const deleteMessage = async (messageId: string) => {
    const message = conversation.value.messages.find(message => message.id === messageId);

    addMessage(
      {
        ...message,
        text: `${t('deleting_message')}...`,
        isDraft: true,
      },
      messageId,
      conversation.value,
      true
    );

    try {
      await ChatService.deleteMessage(conversation.value.id, messageId);

      addMessage(
        {
          ...message,
          text: '',
          metadata: undefined,
          deletedAt: DateTime.now().toMillis(),
          isDraft: false,
        },
        messageId,
        conversation.value,
        true
      );
    } catch {
      addMessage(message, messageId, conversation.value, true);
    }
  };

  const addMessageCreatedListener = (channel: Channel) => {
    pusherClient.listen(
      channel,
      ConversationEvent.MessageCreated,
      (event: { conversation: Partial<Conversation>; message }) => {
        if (!event.message) {
          return;
        }

        const mappedMessage = ChatHelper.mapMessage(event);
        const updatedConversation = ChatHelper.findConversation(
          conversations.value,
          event.conversation.id
        );

        if (!updatedConversation) {
          return;
        }

        addMessage(mappedMessage, mappedMessage.id, updatedConversation, false);
      }
    );
  };

  const addTypingStatusListener = (
    channel: Channel,
    participants: Participant[],
    senders: Participant[]
  ) => {
    pusherClient.listen(
      channel,
      ConversationEvent.Typing,
      (event: {
        conversation: Partial<Conversation>;
        participant: { recipientId?: string; userId?: string };
      }) => {
        const { recipientId: participantId, userId } = event.participant;

        typing.value = [
          ...typing.value,
          {
            conversationId: event.conversation.id,
            participant: ChatHelper.mapChatParticipant(
              { participantId, userId },
              participants,
              senders
            ),
          },
        ];
      }
    );

    pusherClient.listen(
      channel,
      ConversationEvent.StoppedTyping,
      (event: {
        conversation: Partial<Conversation>;
        participant: { recipientId?: string; userId?: string };
      }) => {
        const { recipientId: participantId, userId } = event.participant;

        typing.value = typing.value.filter(({ conversationId, participant }) => {
          const isSameConversation = conversationId === event.conversation.id;
          const isSameParticipant = ChatHelper.isSameParticipant(
            { participantId, userId },
            participant
          );
          return !(isSameConversation && isSameParticipant);
        });
      }
    );
  };

  const addMessageReadListener = (channel: Channel) => {
    pusherClient.listen(channel, ConversationEvent.MessageRead, event => {
      const updatedConversation = ChatHelper.findConversation(
        conversations.value,
        event.conversation.id
      );

      if (!updatedConversation) {
        return;
      }

      const message = updatedConversation.messages.find(({ id }) => id === event.message.id);
      const read = {
        timestamp: DateTime.now().toMillis(),
        participant: { participantId: event.recipientId, userId: event.userId },
      };

      addMessage(
        { ...message, reads: [...message.reads, read] },
        message.id,
        updatedConversation,
        true
      );
    });
  };

  const addMessageDeletedListener = (channel: Channel) => {
    pusherClient.listen(channel, ConversationEvent.MessageDeleted, event => {
      const updatedConversation = ChatHelper.findConversation(
        conversations.value,
        event.conversation.id
      );

      if (!updatedConversation) {
        return;
      }

      const message = updatedConversation.messages.find(({ id }) => id === event.message.id);

      addMessage(
        { ...message, deletedAt: DateTime.now().toMillis(), text: '', metadata: undefined },
        message.id,
        updatedConversation,
        true
      );
    });
  };

  const loadConversation = async (conversationId: string) => {
    try {
      const newConversation = await ChatService.loadConversationDetails(conversationId);

      const conversationExists = ChatHelper.findConversation(conversations.value, conversationId);

      conversations.value = conversationExists
        ? ChatHelper.mapConversations(conversations.value, newConversation)
        : [...conversations.value, newConversation];
    } catch (error) {
      console.error(error);
    }
  };

  const handleCreatedConversation = (event: { conversationId: string }) => {
    const ifConversationExists = ChatHelper.findConversation(
      conversations.value,
      event.conversationId
    );

    if (ifConversationExists) {
      return;
    }

    loadConversation(event.conversationId);
  };

  const addConversationCreatedListener = (channel: Channel) => {
    pusherClient.listen(channel, DealroomEvent.ConversationCreated, handleCreatedConversation);
  };

  return {
    conversation,
    conversations,
    loadingConversationDetails,
    loadingConversationList,
    typing,
    replyMessage,
    error,
    setDefaultConversation,
    loadConversationList,
    loadConversationDetails,
    createMessage,
    setMessageReadBy,
    setTypingStatus,
    deleteMessage,
    addMessageCreatedListener,
    addTypingStatusListener,
    addMessageReadListener,
    addMessageDeletedListener,
    addMessage,
    setReplyMessage,
    setCurrentConversation,
    createConversation,
    handleNewConversationParticipants,
    newConversationParticipants,
    addConversationCreatedListener,
    loadConversation,
    handleCreatedConversation,
    retrySend,
    setOpenConversationPayload,
    openConversationPayload,
  };
});
